// MemberVoices.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Testimonials from existing members. Strict rule from the brief:
//
//   "Do not fabricate live testimonials."
//
// So this section ONLY renders if the TESTIMONIALS array in inclusionContent.js
// has at least one entry. The data file ships empty by default. When you have
// real quotes (with attribution and permission), drop them in and this
// section reveals itself automatically.
//
// Until then: section is invisible. Page stays truthful.
// ─────────────────────────────────────────────────────────────────────────────

import { TESTIMONIALS, getPalette } from '../data/inclusionContent.js';

const GD   = 'var(--font-heading-primary)';
const FS   = 'var(--font-serif-body, Georgia, "Times New Roman", serif)';
const NU   = 'var(--font-body)';
const GOLD = '#c9a96e';

export default function MemberVoices({ darkMode }) {
  const P = getPalette(darkMode);
  // Honest empty-state: section disappears entirely if there are no real
  // testimonials yet. No fabrication, no placeholders bleeding into the
  // public page.
  if (!TESTIMONIALS || TESTIMONIALS.length === 0) return null;

  return (
    <section style={{
      background:  P.altSectionBg,
      padding:     'clamp(80px, 10vw, 140px) clamp(24px, 6vw, 80px)',
      transition:  'background 0.3s ease',
    }}>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        {/* Eyebrow + title */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(48px, 6vw, 80px)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 24 }}>
            <span style={{ width: 36, height: 1, background: GOLD }} />
            <span style={{
              fontFamily: NU, fontSize: 9, fontWeight: 600,
              letterSpacing: '0.32em', textTransform: 'uppercase', color: GOLD,
            }}>
              Voices from the Collection
            </span>
            <span style={{ width: 36, height: 1, background: GOLD }} />
          </div>
          <h2 style={{
            fontFamily: GD,
            fontSize:   'clamp(28px, 4vw, 44px)',
            fontWeight: 400,
            color:      P.text,
            lineHeight: 1.12,
            letterSpacing: '-0.01em',
            margin:     0,
            transition: 'color 0.3s ease',
          }}>
            What members say about being part of LWD.
          </h2>
        </div>

        {/* Quotes, column on mobile, side-by-side on wider viewports */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: TESTIMONIALS.length === 1
            ? '1fr'
            : 'repeat(auto-fit, minmax(min(360px, 100%), 1fr))',
          gap: 'clamp(32px, 3.6vw, 56px)',
        }}>
          {TESTIMONIALS.map((t, i) => (
            <Quote key={i} t={t} P={P} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Single testimonial card ────────────────────────────────────────────────

function Quote({ t, P }) {
  const quote       = t?.quote || '';
  const attribution = t?.attribution || '';
  const location    = t?.location || '';

  return (
    <figure style={{
      margin: 0,
      padding: 'clamp(28px, 3vw, 44px)',
      background: P.surface,
      borderTop: `1px solid ${GOLD}40`,
      position: 'relative',
      transition: 'background 0.3s ease',
    }}>
      {/* Decorative quote mark, large, gold, behind the text */}
      <span aria-hidden style={{
        position: 'absolute',
        top: 'clamp(8px, 1vw, 16px)',
        left: 'clamp(16px, 1.6vw, 24px)',
        fontFamily: GD,
        fontSize: 'clamp(56px, 6vw, 88px)',
        lineHeight: 1,
        color: 'rgba(211,154,33,0.18)',
        fontWeight: 400,
      }}>
        “
      </span>

      <blockquote style={{
        margin: 0,
        position: 'relative',
        fontFamily: FS,
        fontStyle: 'italic',
        fontSize:  'clamp(16px, 1.5vw, 21px)',
        lineHeight: 1.55,
        color: P.text,
        transition: 'color 0.3s ease',
      }}>
        {quote}
      </blockquote>

      <figcaption style={{
        marginTop: 24,
        paddingTop: 16,
        borderTop: `1px solid ${P.border}`,
      }}>
        <div style={{
          fontFamily: NU, fontSize: 11, fontWeight: 600,
          letterSpacing: '0.18em', textTransform: 'uppercase',
          color: P.text,
          transition: 'color 0.3s ease',
        }}>
          {attribution}
        </div>
        {location && (
          <div style={{
            fontFamily: NU, fontSize: 11,
            color: P.muted, marginTop: 4,
            letterSpacing: '0.04em',
          }}>
            {location}
          </div>
        )}
      </figcaption>
    </figure>
  );
}
